import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

const links = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/hybrids', label: 'Hybrids' },
  { to: '/breeding-program', label: 'Breeding Program' },
  { to: '/partners', label: 'Partners' },
]

export function Wordmark({ light = false }: { light?: boolean }) {
  return (
    <Link to="/" className="flex items-center gap-3 group">
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
        <path
          d="M4 3 C13 8, 13 18, 22 23 M22 3 C13 8, 13 18, 4 23"
          stroke={light ? '#F3F5F1' : '#24391C'}
          strokeWidth="1.6"
        />
        <line x1="7.2" y1="8.2" x2="18.8" y2="8.2" stroke="#C98E33" strokeWidth="1.1" />
        <line x1="7.2" y1="17.8" x2="18.8" y2="17.8" stroke="#C98E33" strokeWidth="1.1" />
      </svg>
      <span
        className="text-[15px] font-medium tracking-[0.28em] uppercase"
        style={{ color: light ? '#F3F5F1' : '#24391C' }}
      >
        Hylix
        <span className="ml-2 font-light tracking-[0.28em]" style={{ color: light ? 'rgba(243,245,241,0.55)' : '#96968A' }}>
          Genetics
        </span>
      </span>
    </Link>
  )
}

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const light = !scrolled && !open
  // With the mobile overlay open, the header floats over deep green:
  // keep it transparent and render everything in cream.
  const onDark = light || open

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500"
        style={{
          backgroundColor: scrolled && !open ? 'rgba(243,245,241,0.92)' : 'transparent',
          backdropFilter: scrolled && !open ? 'blur(12px)' : 'none',
          borderBottom: scrolled && !open ? '1px solid #D9DED5' : '1px solid transparent',
        }}
      >
        <div className="container-hx flex h-[72px] items-center justify-between">
          <Wordmark light={onDark} />
          <nav className="hidden items-center gap-9 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-[12px] font-medium uppercase tracking-[0.16em] transition-colors duration-300 ${
                    isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
                  }`
                }
                style={{ color: light ? '#F3F5F1' : '#24391C' }}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/#contact"
              className="px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.16em] transition-colors duration-300"
              style={
                light
                  ? { border: '1px solid rgba(243,245,241,0.45)', color: '#F3F5F1' }
                  : { border: '1px solid #24391C', color: '#24391C' }
              }
            >
              Work With Us
            </Link>
          </nav>
          <button
            className="lg:hidden flex flex-col gap-[6px] p-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            <span
              className="block h-px w-7 transition-[transform,background-color] duration-300"
              style={{
                backgroundColor: onDark ? '#F3F5F1' : '#24391C',
                transform: open ? 'translateY(3.5px) rotate(45deg)' : 'none',
              }}
            />
            <span
              className="block h-px w-7 transition-[transform,background-color] duration-300"
              style={{
                backgroundColor: onDark ? '#F3F5F1' : '#24391C',
                transform: open ? 'translateY(-3.5px) rotate(-45deg)' : 'none',
              }}
            />
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        className="fixed inset-0 z-40 flex flex-col justify-center transition-opacity duration-500 lg:hidden"
        style={{
          backgroundColor: '#24391C',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <nav className="container-hx flex flex-col gap-7">
          {links.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              className="text-3xl font-light text-[#F3F5F1] transition-[opacity,transform] duration-500"
              style={{
                transitionDelay: `${i * 60}ms`,
                opacity: open ? 1 : 0,
                transform: open ? 'translateY(0)' : 'translateY(16px)',
              }}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="mt-8 font-mono-data text-[11px] uppercase tracking-[0.22em] text-[#8BA872]">
            admin@hylix.co.za — South Africa
          </div>
        </nav>
      </div>
    </>
  )
}
